import {useCallback} from 'react';

import {PLATFORM_COLUMN_ID, formatPlatformValue} from 'src/components/user-platform-column/platform-columns';
import {
    useCanadianProvinces,
    useCountries,
    useUsStates,
    useUserStatuses,
    useUserTypes,
} from 'src/hooks/dictionaries';

import {COLUMNS} from '../constants';

import type {UserSearchResult} from 'src/openapi/admin/models/UserSearchResult';

export const useGetColumnValue = () => {
    const {getStateByCode} = useUsStates();
    const {getCountryByCode} = useCountries();
    const {getUserTypeByCode} = useUserTypes();
    const {getUserStatusByCode} = useUserStatuses();
    const {getProvinceByCode} = useCanadianProvinces();

    const getColumnValue = useCallback((columnId: string, user: UserSearchResult) => {
        switch (columnId) {
            case PLATFORM_COLUMN_ID:
                return formatPlatformValue(getUserTypeByCode(user.type)?.description || '',
                    getUserStatusByCode(user.status)?.description || '');
            case COLUMNS.EMAIL:
            case COLUMNS.USER_ID:
            case COLUMNS.ACCOUNT_ID:
            case COLUMNS.GROUP_ID:
            case COLUMNS.FIRST_NAME:
            case COLUMNS.LAST_NAME:
            case COLUMNS.JOB_TITLE:
            case COLUMNS.CITY:
            case COLUMNS.WORK_PHONE:
            case COLUMNS.MOBILE_PHONE:
            case COLUMNS.ADDRESS_1:
            case COLUMNS.ADDRESS_2:
            case COLUMNS.POSTAL_CODE:
                return user[columnId as keyof UserSearchResult] || '';
            case COLUMNS.TYPE:
                return getUserTypeByCode(user.type)?.description || '';
            case COLUMNS.STATUS:
                return getUserStatusByCode(user.status)?.description || '';
            case COLUMNS.COUNTRY:
                return getCountryByCode(user.countryCode)?.description || '';
            case COLUMNS.STATE:
                return getStateByCode(user.stateCode)?.description || '';
            case COLUMNS.PROVINCE:
                return getProvinceByCode(user.provinceCode)?.description || '';
            case COLUMNS.DELETED:
                return user.isDeleted ? 'Yes' : 'No';
            case COLUMNS.TEMPLATE_USER:
                return user.isTemplateUser ? 'Yes' : 'No';
            case COLUMNS.ACCOUNT_TEAM_MEMBER:
                return user.isAccountTeamMember ? 'Yes' : 'No';
            case COLUMNS.FIRST_LICENSE_DATE:
                return user.firstLicenseDate || '';
            default:
                return '';
        }
    }, [getStateByCode, getCountryByCode, getUserTypeByCode, getUserStatusByCode, getProvinceByCode]);

    return {getColumnValue};
};
